import {createContext, useContext, useEffect, useState} from "react";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import VendorInvoicesDetailSkeleton from "./VendorInvoicesDetailSkeleton";
import {VendorInvoicesDetailMain} from "./VendorInvoicesDetailMain";
import {VendorInvoicesDetailProducts} from "./VendorInvoicesDetailProducts";
import {VendorInvoicesDetailServices} from "./VendorInvoicesDetailServices";
import {VendorInvoicesDetailAdditional} from "./VendorInvoicesDetailAdditional";
import {VendorInvoiceDetailPaymentOffsets} from "./VendorInvoiceDetailPaymentOffsets";
import {VendorInvoiceDetailFooter} from "./VendorInvoiceDetailFooter";
import {IVendorInvoiceDto} from "src/dtos/documents/VendorInvoices/IVendorInvoiceDto";
import {IVendorInvoiceModel} from "src/models/Documents/VendorInvoices/IVendorInvoiceModel";
import {NewDocumentModalViewContext} from "src/components/Shared/NewDocumentModalView";
import handleFormSubmit, {DocumentSaveActionType} from "src/helpers/DocumentHelper";
import {AppEntities, AppServices} from "src/config/entities";
import {importFromExcel, saveObjectToExcel} from "src/helpers/SaveObjectToExcel";
import {NFTabs} from "src/components/Shared/NewForms/NFTabs";
import {NFTab} from "src/components/Shared/NewForms/NFTab";
import {DocumentModalViewHeader} from "src/components/Shared/DocumentModalViewHeader";
import {NFTabContainer} from "src/components/Shared/NewForms/NFTabContainer";
import {useFetchVendorInvoice} from "../hooks/fetchDocument";

interface IProps {
    model: IVendorInvoiceDto | null;
    modelUpdatedEvent: (newModel: IVendorInvoiceDto) => void;
}

interface TableRow {
    vendorCustomerName?: string;
    currencyName: string;
    paymentDocumentDescription: string;
    amount: number;
    id: number;
}

type VendorInvoiceContextType = {
    vendorInvoiceDto?: IVendorInvoiceDto;
}

export const VendorInvoiceContext = createContext<VendorInvoiceContextType>({});

export const VendorInvoicesDetail = ({model, modelUpdatedEvent}: IProps) => {
    const [fileName, setFileName] = useState("Vendor Invoices");
    const [vendorInvoiceData, setVendorInvoiceData] = useState<TableRow[]>([]);
    const [availablePayments, setAvailablePayments] = useState([]);

    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors},
        reset,
        formState,
        getValues,
        watch,
        control,
        trigger,
        ...methods
    } = useForm<IVendorInvoiceModel>();

    const companyId = watch("companyId");
    const vendorId = watch("vendorId");
    const date = watch("documentDate");
    const currencyId = watch("currencyId");
    const contractId = watch("contractId");
    const modalViewContext = useContext(NewDocumentModalViewContext);
    const {postHandler, saveHandler, closeHandler, cancelHandler} =
        modalViewContext;
    const [tabIndex, setTabIndex] = useState(0);

    const id = model?.id;
    const {isDocumentLoading} = useFetchVendorInvoice({documentId: id});

    useEffect(() => {
        // if (model) {
        //     setValue("id", model.id);
        //     setValue("baseDocumentAtId", model.baseDocumentAtId);
        //     setValue("documentNumber", model.documentNumber);
        //     setValue("documentDate", model.documentDate);
        //     setValue("companyId", model.companyId);
        //     setValue("warehouseId", model.warehouseId);
        //     setValue("currencyId", model.currencyId);
        //     setValue("vendorId", model.vendorId);
        //     setValue("createdDate", model.createdDate);
        //     setValue("contractId", model.contractId);
        //     setValue("divisionId", model.divisionId);
        //     setValue("comment", model.comment);
        //
        //     setValue("products", model.products);
        //     setValue("services", model.services);
        //     setValue("paymentOffsets", model.paymentOffsets);
        // }
    }, [model]);

    const handleFormSubmitLocal = async (
        action: DocumentSaveActionType,
        data: IVendorInvoiceModel
    ) => {
        await handleFormSubmit(action, data, model, saveHandler, postHandler);
    };

    if (!model) {
        return (
            <>
                <VendorInvoicesDetailSkeleton/>
            </>
        );
    }

    const exportToExcel = () => {
        const data = {
            ...model,
        };
        const documentInfo = {
            title: AppEntities.VendorInvoices.title,
            number: model.documentNumber,
            date: model.documentDate,
        };
        saveObjectToExcel(data, documentInfo);
    };

    const handleUploadFile = (file: File) => {
        importFromExcel(file, (data: any) => {
            reset(data);
        });
    };

    const handleApplyChanges = () => {
        const requestData = {
            date: date,
            companyId: companyId,
            vendorId: vendorId,
            currencyId: currencyId,
            contractId: contractId,
            arApDocumentId: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
        };

        AppServices.Axios.post("/PaymentOffsets/vendor", requestData)
            .then((response) => {
                console.log("Response:", response.data);
                setVendorInvoiceData(response.data);
                setAvailablePayments(response.data);
            })
            .catch((error) => {
                console.error("Error:", error);
            });
    };


    return (
        <VendorInvoiceContext.Provider value={{}}>
            <FormProvider
                getValues={getValues}
                setValue={setValue}
                register={register}
                control={control}
                trigger={trigger}
                reset={reset}
                watch={watch}
                handleSubmit={handleSubmit}
                formState={formState}
                {...methods}
            >
                <NFTabs
                    onTabChange={(newIndex) => {
                        setTabIndex(newIndex);
                    }}
                >
                    <NFTab title={"Main"}/>
                    <NFTab title={"Products"}/>
                    <NFTab title={"Services"}/>
                    <NFTab title={"Payment Offset"}/>
                    <NFTab title={"Additional"}/>
                </NFTabs>

                <div className={"modal-container-height"}>
                    <DocumentModalViewHeader
                        handleApplyChanges={handleApplyChanges}
                        onPost={handleSubmit((data) => handleFormSubmitLocal("post", data))}
                        onSave={handleSubmit((data) => handleFormSubmitLocal("save", data))}
                        onClose={closeHandler}
                        onCancel={cancelHandler}
                        handleUploadFile={handleUploadFile}
                        exportToExcel={exportToExcel}
                        fileName={fileName}
                        setFileName={setFileName}
                    >
                    </DocumentModalViewHeader>
                    <NFTabContainer index={0} currentIndex={tabIndex}>
                        <VendorInvoicesDetailMain/>
                    </NFTabContainer>

                    <NFTabContainer index={1} currentIndex={tabIndex}>
                        <VendorInvoicesDetailProducts/>
                    </NFTabContainer>

                    <NFTabContainer index={2} currentIndex={tabIndex}>
                        <VendorInvoicesDetailServices/>
                    </NFTabContainer>

                    <NFTabContainer index={3} currentIndex={tabIndex}>
                        <VendorInvoiceDetailPaymentOffsets/>
                    </NFTabContainer>

                    <NFTabContainer index={4} currentIndex={tabIndex}>
                        <VendorInvoicesDetailAdditional/>
                    </NFTabContainer>
                </div>
                <VendorInvoiceDetailFooter/>
            </FormProvider>
        </VendorInvoiceContext.Provider>
    );
};
